// @flow
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Tooltip from '@atlaskit/tooltip';
import LoadingButton from '@atlaskit/button/loading-button';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { usePrevious } from 'react-use';
import { WithApolloClient, withApollo } from 'react-apollo';
import Spinner from '@atlaskit/spinner';
import styled, { css } from 'styled-components';
import { gridSize, fontSize } from '@atlaskit/theme';
import { HISTORY_LOGS } from '../../graphql/queries';
import { getJiraIssuesRelatedToComponent, getJiraIssuesRelatedToComponentJql } from '../../services/jira-services';
import IssuesList from './IssuesList';
import HistoryLogsTab from './HistoryLogsTab';

const TABS_INDEXES = Object.freeze({
    RELATED_ISSUES: 0,
    HISTORY_LOGS: 1,
});

const ISSUES_RESOLVED_PERIOD_OPTIONS = [
    { label: 'All linked issues', value: 'all' },
    { label: 'Resolved in past 14 days', value: '14' },
    { label: 'Resolved in past 30 days', value: '30' },
    { label: 'Resolved in past 90 days', value: '90' },
];

const EmptyTabWrapper = styled.div`
    margin-top: 14px;
    display: flex;
    justify-content: center;
    width: 100%;
`;

const DefaultView = styled.div`
    line-height: ${(gridSize() * 2.5) / fontSize()};
    padding: ${gridSize() - 2}px ${gridSize() - 2}px;
    word-break: break-word;
    cursor: text;
    white-space: pre-wrap;

    ${(props) =>
        props.isEmpty &&
        css`
            color: #bbb;
        `}
    ${(props) =>
        props.error &&
        css`
            color: #f44336;
        `}
`;

const TabsWrapper = styled.div`
    position: relative;
    margin-left: calc(-1 * var(--ds-space-100, 8px));
`;

const FetchTabListButtonWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 4px;
`;

type Props = {
    virtualComponentOrVersion: any,
    type: 'component' | 'release',
};

const VirtualComponentTabs = ({ virtualComponentOrVersion, type, client }: WithApolloClient<Props>) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState();
    const [historyLogs, setHistoryLogs] = useState();
    const [isLoadingHistoryLogs, setIsLoadingHistoryLogs] = useState(true);
    const [relatedIssues, setRelatedIssues] = useState();
    const [isLoadingRelatedIssues, setIsLoadingRelatedIssues] = useState(true);
    const [selectedIssuesPeriod, setSelectedIssuesPeriod] = useState(ISSUES_RESOLVED_PERIOD_OPTIONS[0]);

    const issuePropertyKey = useMemo(() => {
        if (type === 'release') {
            return 'Component Versions';
        }
        return 'Component';
    }, [type]);

    const fetchHistoryLogs = useCallback(async () => {
        try {
            setIsLoadingHistoryLogs(true);
            const res = await client.query({
                query: HISTORY_LOGS,
                variables: { objectType: type, objectId: virtualComponentOrVersion.id },
                fetchPolicy: 'network-only',
            });
            setHistoryLogs(res.data.historyLogs);
        } finally {
            setIsLoadingHistoryLogs(false);
        }
    }, [client, type, virtualComponentOrVersion.id]);

    const fetchRelatedIssues = useCallback(async () => {
        try {
            setIsLoadingRelatedIssues(true);
            setRelatedIssues(
                await getJiraIssuesRelatedToComponent({
                    componentName: virtualComponentOrVersion.name,
                    projectsIds: virtualComponentOrVersion.projects?.map(({ jiraProjectId }) => jiraProjectId),
                    issuePropertyKey,
                    daysResolved: selectedIssuesPeriod.value,
                    maxResultsPerPage: 5000,
                }),
            );
        } finally {
            setIsLoadingRelatedIssues(false);
        }
    }, [
        issuePropertyKey,
        selectedIssuesPeriod.value,
        virtualComponentOrVersion.name,
        virtualComponentOrVersion.projects,
    ]);

    const relatedIssuesJql = useMemo(() => {
        return getJiraIssuesRelatedToComponentJql({
            componentName: virtualComponentOrVersion.name,
            projectsIds: virtualComponentOrVersion.projects?.map(({ jiraProjectId }) => jiraProjectId),
            issuePropertyKey,
            daysResolved: selectedIssuesPeriod.value,
        });
    }, [
        issuePropertyKey,
        selectedIssuesPeriod.value,
        virtualComponentOrVersion.name,
        virtualComponentOrVersion.projects,
    ]);

    const previousSelectedTabIndex = usePrevious(selectedTabIndex);
    const previousSelectedIssuesPeriod = usePrevious(selectedIssuesPeriod);

    const fetchSelectedTab = useCallback(
        (force = false) => {
            if (selectedTabIndex === -1) {
                setSelectedTabIndex(TABS_INDEXES.RELATED_ISSUES);
                setHistoryLogs(undefined);
                setIsLoadingHistoryLogs(true);
                setRelatedIssues(undefined);
                setIsLoadingRelatedIssues(true);
                return;
            }

            if (force || selectedTabIndex !== previousSelectedTabIndex) {
                if (selectedTabIndex === TABS_INDEXES.RELATED_ISSUES && (force || !relatedIssues)) {
                    fetchRelatedIssues();
                    return;
                }

                if (selectedTabIndex === TABS_INDEXES.HISTORY_LOGS && (force || !historyLogs)) {
                    fetchHistoryLogs();
                    return;
                }
            }

            if (
                selectedTabIndex === TABS_INDEXES.RELATED_ISSUES &&
                selectedIssuesPeriod !== previousSelectedIssuesPeriod
            ) {
                fetchRelatedIssues();
            }
        },
        [
            fetchHistoryLogs,
            fetchRelatedIssues,
            historyLogs,
            previousSelectedIssuesPeriod,
            previousSelectedTabIndex,
            relatedIssues,
            selectedIssuesPeriod,
            selectedTabIndex,
        ],
    );

    const refetchSelectedTab = useCallback(() => {
        fetchSelectedTab(true);
    }, [fetchSelectedTab]);

    useEffect(() => {
        fetchSelectedTab();
    }, [fetchSelectedTab]);

    const previousVirtualComponentOrVersion = usePrevious(virtualComponentOrVersion);

    useEffect(() => {
        if (virtualComponentOrVersion?.id !== previousVirtualComponentOrVersion?.id) {
            setSelectedTabIndex(-1);
        }
    }, [previousVirtualComponentOrVersion?.id, virtualComponentOrVersion?.id]);

    const isLoadingSelectedTab = useMemo(() => {
        if (selectedTabIndex === TABS_INDEXES.RELATED_ISSUES) {
            return isLoadingRelatedIssues;
        }
        if (selectedTabIndex === TABS_INDEXES.HISTORY_LOGS) {
            return isLoadingHistoryLogs;
        }
        return false;
    }, [isLoadingHistoryLogs, isLoadingRelatedIssues, selectedTabIndex]);

    const relatedIssuesPanel = useMemo(() => {
        if (isLoadingRelatedIssues) {
            return (
                <EmptyTabWrapper>
                    <Spinner />
                </EmptyTabWrapper>
            );
        }
        return (
            <IssuesList
                onPeriodChange={setSelectedIssuesPeriod}
                periodValue={selectedIssuesPeriod}
                periodOptions={ISSUES_RESOLVED_PERIOD_OPTIONS}
                type="component"
                issues={relatedIssues}
                issuesJql={relatedIssuesJql}
            />
        );
    }, [isLoadingRelatedIssues, relatedIssues, relatedIssuesJql, selectedIssuesPeriod]);

    const historyLogsPanel = useMemo(() => {
        if (isLoadingHistoryLogs) {
            return (
                <EmptyTabWrapper>
                    <Spinner />
                </EmptyTabWrapper>
            );
        }
        if (historyLogs?.length) {
            return <HistoryLogsTab historyLogs={historyLogs} component={virtualComponentOrVersion} />;
        }
        return (
            <EmptyTabWrapper>
                <DefaultView isEmpty>No history logs were found.</DefaultView>
            </EmptyTabWrapper>
        );
    }, [historyLogs, isLoadingHistoryLogs, virtualComponentOrVersion]);

    return (
        <TabsWrapper>
            <Tabs selected={selectedTabIndex} onChange={setSelectedTabIndex}>
                <TabList>
                    <Tab>Linked issues</Tab>
                    {type === 'component' && <Tab>History</Tab>}
                </TabList>
                <TabPanel>{relatedIssuesPanel}</TabPanel>
                {type === 'component' && <TabPanel>{historyLogsPanel}</TabPanel>}
            </Tabs>
            <FetchTabListButtonWrapper>
                <Tooltip content="refresh list" key="refresh_list">
                    <LoadingButton
                        spacing="compact"
                        isLoading={isLoadingSelectedTab}
                        appearance="subtle"
                        onClick={refetchSelectedTab}
                    >
                        <RefreshIcon size="medium" />
                    </LoadingButton>
                </Tooltip>
            </FetchTabListButtonWrapper>
        </TabsWrapper>
    );
};

export default withApollo(VirtualComponentTabs);
